// src/components/appSettings/Collection/CollectionForm/CollectionFormSections.jsx
import React from "react";
import { FiUpload, FiX, FiInfo } from "react-icons/fi";

// ============= BASIC INFO SECTION =============
export const CollectionBasicInfoSection = ({ formData, errors, onChange }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-4">Basic Information</h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Collection Name *
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={onChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.name ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Enter collection name"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-500">{errors.name}</p>
        )}
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Slug *
        </label>
        <input
          type="text"
          name="slug"
          value={formData.slug}
          onChange={onChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.slug ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="collection-slug"
        />
        {errors.slug && (
          <p className="mt-1 text-sm text-red-500">{errors.slug}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          URL-friendly identifier (lowercase, hyphens only)
        </p>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Short Description
        </label>
        <input
          type="text"
          name="shortDescription"
          value={formData.shortDescription}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Brief description for cards"
          maxLength={150}
        />
        <p className="text-xs text-gray-500 mt-1">
          {formData.shortDescription?.length || 0}/150 characters
        </p>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={onChange}
          rows="4"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Detailed collection description"
        />
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">Active</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="isFeatured"
            checked={formData.isFeatured}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            Featured
          </span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="isExclusive"
            checked={formData.isExclusive}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            Exclusive
          </span>
        </label>
      </div>
    </div>
  </div>
);

// ============= MEDIA SECTION =============
export const CollectionMediaSection = ({
  formData,
  imageFiles,
  imagePreviews,
  errors,
  onImageUpload,
  onImageRemove,
  mode,
}) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-4">Collection Images</h3>
    <p className="text-sm text-gray-500 mb-4">
      Upload images for different display contexts. Thumbnail is required.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Hero Image */}
      <ImageUploadBox
        label="Hero Image"
        type="hero"
        preview={imagePreviews.hero}
        onUpload={onImageUpload}
        onRemove={onImageRemove}
        error={errors.heroImage}
        recommended="Recommended: 1200x600px"
      />

      {/* Thumbnail Image */}
      <ImageUploadBox
        label="Thumbnail Image *"
        type="thumbnail"
        preview={imagePreviews.thumbnail}
        onUpload={onImageUpload}
        onRemove={onImageRemove}
        error={errors.thumbnailImage}
        recommended="Recommended: 400x400px"
      />

      {/* Banner Image */}
      <ImageUploadBox
        label="Banner Image"
        type="banner"
        preview={imagePreviews.banner}
        onUpload={onImageUpload}
        onRemove={onImageRemove}
        error={errors.bannerImage}
        recommended="Recommended: 1920x400px"
      />

      {/* Icon Image */}
      <ImageUploadBox
        label="Icon Image"
        type="icon"
        preview={imagePreviews.icon}
        onUpload={onImageUpload}
        onRemove={onImageRemove}
        error={errors.iconImage}
        recommended="Recommended: 128x128px"
      />
    </div>
  </div>
);

// Image Upload Box Component
const ImageUploadBox = ({
  label,
  type,
  preview,
  onUpload,
  onRemove,
  error,
  recommended,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <div
      className={`border-2 border-dashed rounded-lg overflow-hidden ${
        error ? "border-red-500" : "border-gray-300"
      }`}
    >
      {preview ? (
        <div className="relative">
          <img src={preview} alt={label} className="w-full h-40 object-cover" />
          <button
            type="button"
            onClick={() => onRemove(type)}
            className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
          >
            <FiX size={16} />
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center h-40 cursor-pointer hover:bg-gray-50">
          <FiUpload className="text-3xl text-gray-400 mb-2" />
          <span className="text-sm text-gray-500">Click to upload</span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onUpload(type, e.target.files[0])}
            className="hidden"
          />
        </label>
      )}
    </div>
    {recommended && <p className="text-xs text-gray-500 mt-1">{recommended}</p>}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

// ============= FILTERS SECTION =============
export const CollectionFiltersSection = ({
  formData,
  errors,
  categories,
  onChange,
  onArrayChange,
}) => {
  // Matches PRODUCT_SUBTYPE_ENUM from backend
  const productTypes = [
    "RING",
    "NECKLACE",
    "EARRING",
    "BRACELET",
    "PENDANT",
    "CHAIN",
    "BANGLE",
    "ANKLET",
    "NOSE_PIN",
    "MANGALSUTRA",
    "KADA",
    "COIN",
    "BAR",
    "IDOL",
    "OTHER",
  ];

  // Matches PRODUCT_TYPE_ENUM from backend
  const metalTypes = ["GOLD", "SILVER", "PLATINUM", "DIAMOND", "STUDDED"];

  // Matches PURITY_ENUM from backend
  const purities = ["24K", "22K", "18K", "14K", "925", "950", "PT950"];

  const handleCategoryToggle = (categoryId) => {
    const current = formData.filters.categories || [];
    const updated = current.includes(categoryId)
      ? current.filter((c) => c !== categoryId)
      : [...current, categoryId];
    onArrayChange("filters.categories", updated);
  };

  const handleProductTypeToggle = (type) => {
    const current = formData.filters.productTypes || [];
    const updated = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    onArrayChange("filters.productTypes", updated);
  };

  const handleMetalTypeToggle = (metal) => {
    const current = formData.filters.metalTypes || [];
    const updated = current.includes(metal)
      ? current.filter((m) => m !== metal)
      : [...current, metal];
    onArrayChange("filters.metalTypes", updated);
  };

  const handlePurityToggle = (purity) => {
    const current = formData.filters.purities || [];
    const updated = current.includes(purity)
      ? current.filter((p) => p !== purity)
      : [...current, purity];
    onArrayChange("filters.purities", updated);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4">Product Filters</h3>
      <p className="text-sm text-gray-500 mb-4">
        Define which products should be included in this collection
      </p>

      <div className="space-y-6">
        {/* Categories */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categories
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {categories?.map((category) => (
              <label
                key={category._id}
                className="flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.filters.categories?.includes(category._id)}
                  onChange={() => handleCategoryToggle(category._id)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm">{category.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Product Types */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Product Types
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {productTypes.map((type) => (
              <label
                key={type}
                className="flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.filters.productTypes?.includes(type)}
                  onChange={() => handleProductTypeToggle(type)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm">{type}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Metal Types */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Metal Types
          </label>
          <div className="flex flex-wrap gap-2">
            {metalTypes.map((metal) => (
              <label
                key={metal}
                className="flex items-center px-3 py-2 border rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.filters.metalTypes?.includes(metal)}
                  onChange={() => handleMetalTypeToggle(metal)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm">{metal}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Purities */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Purities
          </label>
          <div className="flex flex-wrap gap-2">
            {purities.map((purity) => (
              <label
                key={purity}
                className="flex items-center px-3 py-2 border rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.filters.purities?.includes(purity)}
                  onChange={() => handlePurityToggle(purity)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm">{purity}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Price Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price Range
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                type="number"
                name="filters.priceRange.min"
                value={formData.filters.priceRange?.min || ""}
                onChange={onChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Min price"
              />
            </div>
            <div>
              <input
                type="number"
                name="filters.priceRange.max"
                value={formData.filters.priceRange?.max || ""}
                onChange={onChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Max price"
              />
            </div>
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags (comma separated)
          </label>
          <input
            type="text"
            value={formData.filters.tags?.join(", ") || ""}
            onChange={(e) => {
              const tags = e.target.value
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);
              onArrayChange("filters.tags", tags);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="tag1, tag2, tag3"
          />
        </div>
      </div>
    </div>
  );
};

// ============= DISPLAY SECTION =============
export const CollectionDisplaySection = ({ formData, errors, onChange }) => {
  const cardStyles = ["STANDARD", "GRADIENT", "IMAGE_OVERLAY", "MINIMAL"];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4">Display Settings</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority
          </label>
          <input
            type="number"
            name="display.priority"
            value={formData.display.priority}
            onChange={onChange}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            Higher number = higher priority
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Position
          </label>
          <input
            type="number"
            name="display.position"
            value={formData.display.position || ""}
            onChange={onChange}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Optional"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Card Style
          </label>
          <select
            name="display.cardStyle"
            value={formData.display.cardStyle}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {cardStyles.map((style) => (
              <option key={style} value={style}>
                {style.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Text Color
          </label>
          <input
            type="color"
            name="display.textColor"
            value={formData.display.textColor || "#000000"}
            onChange={onChange}
            className="w-full h-10 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Badge Text
          </label>
          <input
            type="text"
            name="display.badgeText"
            value={formData.display.badgeText}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="e.g., NEW, SALE"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Badge Color
          </label>
          <input
            type="color"
            name="display.badgeColor"
            value={formData.display.badgeColor || "#FF0000"}
            onChange={onChange}
            className="w-full h-10 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="mt-4 flex gap-6">
        <label className="flex items-center">
          <input
            type="checkbox"
            name="display.showOnHome"
            checked={formData.display.showOnHome}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            Show on Home Screen
          </span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="display.showInMenu"
            checked={formData.display.showInMenu}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            Show in Menu
          </span>
        </label>
      </div>
    </div>
  );
};

// ============= METADATA SECTION =============
export const CollectionMetadataSection = ({ formData, errors, onChange }) => {
  // Matches OCCASION_ENUM from backend
  const occasions = [
    "WEDDING",
    "ENGAGEMENT",
    "ANNIVERSARY",
    "BIRTHDAY",
    "FESTIVAL",
    "GIFT",
    "INVESTMENT",
  ];
  const seasons = ["SPRING", "SUMMER", "FALL", "WINTER", "ALL_SEASON"];
  // Matches PRODUCT_GENDER_ENUM from backend
  const genders = ["MALE", "FEMALE", "UNISEX", "KIDS"];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4">Metadata</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Occasion
          </label>
          <select
            name="metadata.occasion"
            value={formData.metadata.occasion}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select occasion</option>
            {occasions.map((occasion) => (
              <option key={occasion} value={occasion}>
                {occasion}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Season
          </label>
          <select
            name="metadata.season"
            value={formData.metadata.season}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select season</option>
            {seasons.map((season) => (
              <option key={season} value={season}>
                {season.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target Gender
          </label>
          <select
            name="metadata.targetGender"
            value={formData.metadata.targetGender}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select gender</option>
            {genders.map((gender) => (
              <option key={gender} value={gender}>
                {gender}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Age Group
          </label>
          <input
            type="text"
            name="metadata.ageGroup"
            value={formData.metadata.ageGroup}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="e.g., 18-35, Kids"
          />
        </div>
      </div>
    </div>
  );
};

// ============= TARGETING SECTION =============
export const CollectionTargetingSection = ({
  formData,
  errors,
  branches,
  onChange,
  onArrayChange,
}) => {
  const handleBranchToggle = (branchCode) => {
    const current = formData.targeting.branches || [];
    const updated = current.includes(branchCode)
      ? current.filter((b) => b !== branchCode)
      : [...current, branchCode];
    onArrayChange("targeting.branches", updated);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4">Targeting</h3>

      <div className="space-y-4">
        {/* Branches */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Branches
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Leave empty to show on all branches
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {branches?.map((branch) => (
              <label
                key={branch.code}
                className="flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.targeting.branches?.includes(branch.code)}
                  onChange={() => handleBranchToggle(branch.code)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm">
                  {branch.name} ({branch.code})
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Validity Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valid From
            </label>
            <input
              type="date"
              name="validFrom"
              value={formData.validFrom || ""}
              onChange={onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valid Until
            </label>
            <input
              type="date"
              name="validUntil"
              value={formData.validUntil || ""}
              onChange={onChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.validUntil ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.validUntil && (
              <p className="mt-1 text-sm text-red-500">{errors.validUntil}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= SEO SECTION =============
export const CollectionSEOSection = ({
  formData,
  errors,
  onChange,
  onArrayChange,
}) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-4">SEO Settings</h3>
    <p className="text-sm text-gray-500 mb-4">
      Optimize your collection for search engines
    </p>

    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          SEO Title
        </label>
        <input
          type="text"
          name="seo.title"
          value={formData.seo.title}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="SEO optimized title"
          maxLength={60}
        />
        <p className="text-xs text-gray-500 mt-1">
          {formData.seo.title?.length || 0}/60 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          SEO Description
        </label>
        <textarea
          name="seo.description"
          value={formData.seo.description}
          onChange={onChange}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="SEO meta description"
          maxLength={160}
        />
        <p className="text-xs text-gray-500 mt-1">
          {formData.seo.description?.length || 0}/160 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Keywords (comma separated)
        </label>
        <input
          type="text"
          value={formData.seo.keywords?.join(", ") || ""}
          onChange={(e) => {
            const keywords = e.target.value
              .split(",")
              .map((k) => k.trim())
              .filter(Boolean);
            onArrayChange("seo.keywords", keywords);
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="keyword1, keyword2, keyword3"
        />
      </div>

      <div className="bg-blue-50 rounded-lg p-3 flex items-start gap-2">
        <FiInfo className="text-blue-600 mt-0.5" />
        <p className="text-xs text-blue-800">
          Good SEO practices: Use unique titles and descriptions, include
          relevant keywords naturally, keep titles under 60 characters and
          descriptions under 160 characters.
        </p>
      </div>
    </div>
  </div>
);
